import { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Label, Dropdown, Segment, Search } from 'semantic-ui-react';

import { get } from '../../../../utils/lodash';

import {
  CustomTable,
  EmptyContainer,
  CustomSegment,
} from '../../../../components';

import {
  searchOrganisationActivities,
  loginAsUser,
  getUserProfile,
} from '../../../../store/actions';

import { userAuthMgr } from '../../../../utils/auth';

import './OrganizationAllActivities.scss';

let OrganizationAllActivities = ({
  t,
  searchOrganisationActivities,
  loginAsUser,
  getUserProfile,
  user,
  isSuperAdmin,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(
    searchParams.get('q') || ''
  );
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page')) || 1
  );
  const [filter, setFilter] = useState(searchParams.get('status') || 'all');
  const [actType, setActType] = useState(searchParams.get('type') || 'all');
  const [attributeName, setAttributeName] = useState('name');
  const [order] = useState('asc');

  const navigate = useNavigate();

  // Debounce the search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setSearchParams({
        q: searchTerm,
        page: currentPage,
        status: filter,
        type: actType,
      });
    }, 800);

    return () => clearTimeout(handler);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const performSearch = async (page = 1) => {
    try {
      setLoading(true);

      const result = await searchOrganisationActivities(
        debouncedSearchTerm,
        page,
        20,
        filter,
        actType,
        attributeName,
        order
      );

      setActivities(result?.rows || []);
      setTotalPages(result?.totalPages || 0);
      setCurrentPage(page);

      setSearchParams({
        q: debouncedSearchTerm,
        page,
        status: filter,
        type: actType,
      });
    } catch (error) {
      console.error('Search error:', error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    performSearch(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, filter, actType]);

  const handleSearchChange = (e, { value }) => {
    setSearchTerm(value);
    if (/^\d+$/.test(value.trim())) {
      setAttributeName('id');
    } else {
      setAttributeName('name');
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setDebouncedSearchTerm(searchTerm);
  };

  const getStatusLabel = (t, statusId) => {
    switch (statusId) {
      default:
      case 1:
        return { text: t('activity_status.in_development'), class: 'warning' };
      case 2:
        return { text: t('account_status.active'), class: 'success' };
      case 3:
        return { text: t('activity_status.completed'), class: 'completed' };
    }
  };

  const statusOptions = (t) => [
    {
      key: 'All Activities',
      text: t('activity_status.all_activities'),
      value: 'all',
    },
    {
      key: 'Ongoing',
      text: t('activity_status.ongoing_activities'),
      value: 'ongoing',
    },
    {
      key: 'Completed',
      text: t('activity_status.completed_activities'),
      value: 'completed',
    },
  ];

  const actTypeOptions = (t) => [
    { key: 'All', text: t('activity_type.all_types'), value: 'all' },
    { key: 'Single', text: t('activity_type.single'), value: 'single' },
    { key: 'Grant', text: t('activity_type.grant'), value: 'grant' },
    { key: 'Festival', text: t('activity_type.festival'), value: 'festival' },
    {
      key: 'Multi Activity',
      text: t('activity_type.multi_activity'),
      value: 'multi-activity',
    },
  ];

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handleActTypeChange = (newActType) => {
    setActType(newActType);
  };

  const handlePageChange = (page) => {
    performSearch(page);
  };

  const doLoginAs = async (activityId, userId) => {
    try {
      await loginAsUser(userId);
      await userAuthMgr.authenticate();
      await getUserProfile();
      navigate(`/activities/${activityId}`);
    } catch (error) {
      console.error('LOGIN AS ~ error', error);
    }
  };

  const columns = [
    /******
    {
      title: (
        <div className="user-act-heading-container-custom-feature">
          <strong>ID</strong>
        </div>
      ),
      render: (data) => {
        return <div className="activity-id">#{get(data, 'id', 'N/A')}</div>;
      },
      width: 2,
    },
    *********/
    {
      title: (
        <div className="user-act-heading-container-custom-feature">
          <strong>{t('common.activity_name')}</strong>
        </div>
      ),
      render: (data) => {
        return (
          <>
            <Link to={`/activities/${data.id}`}>
              {data?.name || t('common.not_applicable')}
            </Link>
            <span className="activity-id">#{data.id}</span>
          </>
        );
      },
      width: 5,
    },
    {
      title: (
        <div className="user-act-heading-container-custom-feature">
          <strong>{t('common.owner')}</strong>
        </div>
      ),
      render: (data) => {
        return <div>{get(data, 'owner.name', '-')}</div>;
      },
      width: 4,
    },
    {
      title: (
        <div className="user-act-heading-container-custom-feature">
          <strong>{t('common.status')}</strong>
        </div>
      ),
      render: (data) => {
        const status = getStatusLabel(t, get(data, 'statusId', 1));
        return (
          <Label className={`status-label ${status.class}`}>
            {status.text}
          </Label>
        );
      },
      width: 3,
    },
    {
      title: (
        <div className="user-act-heading-container-custom-feature">
          <strong></strong>
        </div>
      ),
      render: (data) => {
        return (
          <div className="owned">
            <Dropdown
              icon="ellipsis vertical"
              direction="left"
              inline
              selectOnBlur={false}
            >
              <Dropdown.Menu>
                <Dropdown.Item
                  text={t('common.view_activity')}
                  className="dropdown-customItem"
                >
                  <Link to={`/organisation/activity/${data.id}`}>
                    {t('common.view_activity')}
                  </Link>
                </Dropdown.Item>

                {isSuperAdmin && data.owner && !data.owner.disabled && (
                  <Dropdown.Item
                    text={`${t('common.login_as')}...`}
                    className="dropdown-customItem"
                  >
                    <span
                      className="login-as"
                      onClick={(e) => doLoginAs(data.id, data.owner.id)}
                    >
                      {t('common.login_as')} {data.owner.name} (#{data.owner.id}
                      ) {t('common.and_go_to_activity')}
                    </span>
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        );
      },
      width: 2,
    },
  ];

  const renderTable = () => {
    return (
      <CustomTable
        customClass="super-admin-table home-activity"
        columns={columns}
        data={activities}
        emptyMessage={
          searchTerm.trim() || filter !== 'all' || actType !== 'all'
            ? t('activity.search.no_activities_found_matching_filters')
            : t('activity.search.no_activities_found')
        }
        keyExtractor={(item) => item.id}
        loading={loading}
        pagination
        handlePaginationChange={handlePageChange}
        page={currentPage}
        noOfPages={totalPages}
        header
      />
    );
  };

  return (
    <div className="admin-activities-container">
      <CustomSegment
        title={t('common.all_activities')}
        subChild={
          <div className="sub-child-segment navbar">
            <form
              onSubmit={handleSearchSubmit}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <Search
                className="search-bar-custom"
                placeholder={t('activity.search.search_by_activity_name_or_id')}
                showNoResults={false}
                onSearchChange={handleSearchChange}
                value={searchTerm}
                loading={loading}
              />
              <Button
                type="submit"
                className="Primary-Button"
                loading={loading}
                style={{ marginLeft: '10px' }}
              >
                {t('actions.search')}
              </Button>
            </form>
            <div className="activities-filter-container manage-activity-drop">
              <span className="activities-filter">
                <Dropdown
                  direction="left"
                  inline
                  selectOnBlur={false}
                  options={actTypeOptions(t)}
                  value={actType}
                  onChange={(e, { value }) => handleActTypeChange(value)}
                />
              </span>
              <span className="activities-filter">
                <Dropdown
                  direction="left"
                  inline
                  selectOnBlur={false}
                  options={statusOptions(t)}
                  value={filter}
                  onChange={(e, { value }) => handleFilterChange(value)}
                />
              </span>
            </div>
          </div>
        }
        extraMargin
      >
        <Segment
          className="content-segment content-segment-bg"
          loading={loading}
          style={{ marginTop: '20px' }}
        >
          {activities && activities.length ? (
            renderTable()
          ) : (
            <EmptyContainer msg={t('common.no_activities')} />
          )}
        </Segment>
      </CustomSegment>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.authentication.user,
    isSuperAdmin: state.authentication.isSuperAdmin,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      searchOrganisationActivities,
      loginAsUser,
      getUserProfile,
    },
    dispatch
  );
};

OrganizationAllActivities = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(OrganizationAllActivities));

export { OrganizationAllActivities };
